import React, { useState } from 'react';

import { useSnackbar } from 'notistack';

import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditCalendarIcon from '@mui/icons-material/EditCalendar';
import TourIcon from '@mui/icons-material/Tour';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';

import PageSubheader from '~/components/Common/Elements/PageSubheader';

import { useDeleteBundleComponent } from '~/hooks/apis/bundle/useBundles';
import { useGetTourOfferSummary } from '~/hooks/apis/tours/useTourOffers';

import { formatDateShort } from '~/services/TimeService';

import { Bundle } from '~/types/bundles/bundle';

import TourComponentDialog from './TourComponentDialog';
import TourOfferName from './TourOfferName';
import useBundleTourSpan from './useBundleTourSpan';

interface Props {
  bundle: Bundle;
}

const MAX_DATE_CHIPS = 24;

function pluralise(count: number, noun: string) {
  return `${count} ${noun}${count === 1 ? '' : 's'}`;
}

export default function BundleTourSection({ bundle }: Props) {
  const { enqueueSnackbar } = useSnackbar();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isRemoveOpen, setIsRemoveOpen] = useState(false);

  const { tour, startDay: dayOffset, tourDays } = useBundleTourSpan(bundle);
  // Approval swaps the base tour for the LTLE made from it.
  const { data: tourOffer } = useGetTourOfferSummary(tour?.tour_offer_id);
  const deleteComponent = useDeleteBundleComponent(bundle.id);

  const isDraft = bundle.status === 'draft';

  const handleRemove = async () => {
    try {
      await deleteComponent.mutateAsync(tour.id);
      setIsRemoveOpen(false);
      enqueueSnackbar('Tour removed', { variant: 'success' });
    } catch (err) {
      enqueueSnackbar(err.message, { variant: 'error' });
    }
  };

  return (
    <Box>
      <PageSubheader title="Tour" />

      {!tour && (
        <Paper variant="outlined" sx={{ borderStyle: 'dashed', p: 5, textAlign: 'center' }}>
          <Stack spacing={2} alignItems="center">
            <Avatar sx={{ bgcolor: 'primary.main', width: 48, height: 48 }}>
              <TourIcon />
            </Avatar>
            <Box>
              <Typography variant="h6">Start with a base tour</Typography>
              <Typography color="text.secondary">
                Pick the tour this bundle’s LTLE will be made from, then choose which departures to sell.
              </Typography>
            </Box>
            {isDraft && (
              <Button variant="contained" startIcon={<AddIcon />} onClick={() => setIsDialogOpen(true)}>
                Add tour
              </Button>
            )}
          </Stack>
        </Paper>
      )}

      {!!tour && (
        <Card variant="outlined">
          <CardHeader
            avatar={
              <Avatar sx={{ bgcolor: 'primary.main' }}>
                <TourIcon />
              </Avatar>
            }
            title={
              <Typography variant="h6">
                <TourOfferName tourOfferId={tour.tour_offer_id} />
              </Typography>
            }
            subheader={
              tourOffer?.isLtle
                ? 'LTLE · created from the base tour when the bundle was approved'
                : 'Base tour · its LTLE is created when the bundle is approved'
            }
            action={
              isDraft && (
                <Stack direction="row" spacing={1} alignItems="center">
                  <Button startIcon={<EditCalendarIcon />} onClick={() => setIsDialogOpen(true)}>
                    Edit departures
                  </Button>
                  <Tooltip title="Remove tour">
                    <IconButton aria-label="Remove tour" onClick={() => setIsRemoveOpen(true)}>
                      <DeleteOutlineIcon />
                    </IconButton>
                  </Tooltip>
                </Stack>
              )
            }
          />
          <Divider />
          <CardContent>
            <Stack spacing={3}>
              <Grid container spacing={3}>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <Typography variant="caption" color="text.secondary">
                    Runs
                  </Typography>
                  <Typography>
                    {tourDays
                      ? `Day ${dayOffset} to day ${dayOffset + tourDays - 1} (${pluralise(tourDays, 'day')})`
                      : `From day ${dayOffset}`}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <Typography variant="caption" color="text.secondary">
                    Departures selling
                  </Typography>
                  <Typography>{pluralise(bundle.departure_dates.length, 'departure')}</Typography>
                </Grid>
              </Grid>

              {bundle.departure_dates.length === 0 ? (
                <Alert
                  severity="warning"
                  action={
                    isDraft && (
                      <Button color="inherit" size="small" onClick={() => setIsDialogOpen(true)}>
                        Choose departures
                      </Button>
                    )
                  }
                >
                  No departures selected yet. The bundle can’t be approved until it sells at least one.
                </Alert>
              ) : (
                <Stack direction="row" flexWrap="wrap" gap={1}>
                  {bundle.departure_dates.slice(0, MAX_DATE_CHIPS).map((date) => (
                    <Chip key={date} label={formatDateShort(date)} variant="outlined" />
                  ))}
                  {bundle.departure_dates.length > MAX_DATE_CHIPS && (
                    <Chip label={`+${bundle.departure_dates.length - MAX_DATE_CHIPS} more`} />
                  )}
                </Stack>
              )}
            </Stack>
          </CardContent>
        </Card>
      )}

      <TourComponentDialog
        bundle={bundle}
        component={tour}
        open={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
      />

      <Dialog open={isRemoveOpen} onClose={() => setIsRemoveOpen(false)}>
        <DialogTitle>Remove tour?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Its departures, prices and price adjustments are removed with it. You can add a tour again afterwards.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsRemoveOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleRemove} disabled={deleteComponent.isPending}>
            Remove
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
